import React, { useEffect, useMemo } from 'react';
import { Tooltip } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Monitor,
  Smartphone,
  Tablet,
  HelpCircle,
  ArrowRight,
  Files,
} from 'lucide-react';

import DataTable from '../../../../../../shared/designSystem/components/DataTable';
import AttributionIcon from '../../../../../../shared/designSystem/components/AttributionIcon';
import SessionJourneyExpand from './SessionJourneyExpand';
import { SESSIONS_TOOLTIP } from '../../../../../../shared/constants/tooltips';

// =============================================================================
// SessionsTable (V2) — facelifted off legacy AntD `Card` + `Table` chrome to
// the shared `DataTable` primitive. Mirrors V1 SessionsTable's 6-column
// compact pattern (Session + Time / Duration / Pages / Flow / Device /
// Attribution).
//
// V2 differences from V1:
//   • Field shape: `start` / `end` / `pageViews` / `deviceType` /
//     `attrMethod` / `key` (vs V1's `sessionStart` / `sessionEnd` /
//     journey-derived pageViews / `device` / `attributionMethod` /
//     `sessionId`).
//   • Lazy expand — V2 doesn't ship impressions in the row payload.
//     `LazyJourneyExpand` mounts when the row expands, kicks off
//     `fetchSessionDetails`, and pulls the result from
//     `expandedRowsData[record.key]` populated by the parent.
//   • Pagination — V2 connection-paginated; page index lives in the route
//     param `pageNumber` (e.g. `/campaigns/.../sessions/:pageNumber`).
//     Pagination changes update segment 4 of the pathname.
// =============================================================================

const secondsToTimeText = (seconds) => {
  if (!seconds || seconds < 0) return '0s';

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];

  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

const DeviceIcon = ({ device }) => {
  if (device === 'Mobile') return <Smartphone size={14} strokeWidth={1.75} />;
  if (device === 'Tablet') return <Tablet size={14} strokeWidth={1.75} />;
  return <Monitor size={14} strokeWidth={1.75} />;
};

const DATE_TIME_FORMAT = 'MMM DD, YYYY · HH:mm:ss';
const TIME_FORMAT = 'HH:mm:ss';
const formatTimeRange = (start, end) => {
  if (!start) return '—';
  const s = moment(start);
  if (!s.isValid()) return '—';
  const e = end ? moment(end) : null;
  if (!e || !e.isValid()) return s.format(DATE_TIME_FORMAT);
  if (s.isSame(e, 'day')) {
    return `${s.format(DATE_TIME_FORMAT)} → ${e.format(TIME_FORMAT)}`;
  }
  return `${s.format(DATE_TIME_FORMAT)} → ${e.format(DATE_TIME_FORMAT)}`;
};

const sortMoment = (key) => (rowA, rowB) => {
  const a = rowA.original[key];
  const b = rowB.original[key];
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return moment(a).valueOf() - moment(b).valueOf();
};

const sortNumber = (key) => (rowA, rowB) =>
  (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);

const SessionCell = ({ sessionId, start, end }) => (
  <div className="campaign-sessions-v1__session-cell">
    {sessionId ? (
      <Tooltip overlayClassName="mjl-ds" title={sessionId}>
        <span className="campaign-sessions-v1__session-id">{sessionId}</span>
      </Tooltip>
    ) : (
      <span className="campaign-sessions-v1__session-id">—</span>
    )}
    <span className="campaign-sessions-v1__session-time">
      {formatTimeRange(start, end)}
    </span>
  </div>
);

const FlowCell = ({ landing, exit }) => {
  if (!landing && !exit) return <span>—</span>;
  return (
    <div className="campaign-sessions-v1__flow-cell">
      <Tooltip overlayClassName="mjl-ds" title={landing || '—'}>
        <span className="campaign-sessions-v1__flow-page">{landing || '—'}</span>
      </Tooltip>
      <ArrowRight
        size={12}
        strokeWidth={2}
        className="campaign-sessions-v1__flow-arrow"
      />
      <Tooltip overlayClassName="mjl-ds" title={exit || '—'}>
        <span className="campaign-sessions-v1__flow-page">{exit || '—'}</span>
      </Tooltip>
    </div>
  );
};

const PagesBadge = ({ count }) => (
  <span className="campaign-sessions-v1__pages-badge">
    <Files size={12} strokeWidth={1.75} />
    <span>{count}</span>
  </span>
);

// Lazy expand wrapper — when DataTable mounts the expanded row, we fire
// `fetchSessionDetails` for that session if the data isn't already cached
// in `expandedRowsData[recordKey]`. The loader's onCompleted (in the
// parent's loader) updates `expandedRowsData`, which re-renders this with
// the impressions + journey populated. Session metadata (start, duration,
// device, landing/exit page) comes off the row record itself — already
// known before expand, no fetch required for that.
const LazyJourneyExpand = ({
  record,
  expandedRowsData,
  setExpandedRowsData,
  fetchSessionDetails,
  sessionDetailsLoading,
}) => {
  const recordKey = record.key;

  useEffect(() => {
    if (!expandedRowsData[recordKey]) {
      if (setExpandedRowsData) {
        setExpandedRowsData((prev) => ({
          ...prev,
          [recordKey]: { loading: true, impressions: [], sessionJourney: [] },
        }));
      }
      if (fetchSessionDetails) {
        fetchSessionDetails({ variables: { where: { key: recordKey } } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordKey]);

  const rowData = expandedRowsData[recordKey] || {
    loading: true,
    impressions: [],
    sessionJourney: [],
  };

  return (
    <SessionJourneyExpand
      impressions={rowData.impressions || []}
      journey={rowData.sessionJourney || []}
      loading={rowData.loading || sessionDetailsLoading}
      sessionStart={record.start}
      durationInSeconds={record.durationInSeconds}
      landingPage={record.landingPage}
      exitPage={record.exitPage}
      device={record.deviceType}
      pageViewsHint={record.pageViews}
    />
  );
};

const SessionsTable = ({
  data = [],
  loading = false,
  total,
  history,
  location,
  match,
  fetchSessionDetails,
  expandedRowsData = {},
  setExpandedRowsData,
  sessionDetailsLoading,
}) => {
  const { params } = match;
  const { pageNumber } = params;
  const urlParams = new URLSearchParams(location.search);
  const urlPageSize = parseInt(urlParams.get('pageSize'), 10);
  const pageSize = Number.isFinite(urlPageSize) && urlPageSize > 0 ? urlPageSize : 10;
  const currentPage = parseInt(pageNumber, 10) || 1;

  const rows = useMemo(
    () => (data || []).map((d, i) => ({ ...d, __key: d.key || `s-${i}` })),
    [data]
  );

  const columns = useMemo(
    () => [
      {
        id: 'session',
        accessorKey: 'start',
        header: 'Session',
        sortable: true,
        sortingFn: sortMoment('start'),
        meta: { align: 'left', width: 200 },
        cell: ({ row }) => (
          <SessionCell
            sessionId={row.original.sessionId}
            start={row.original.start}
            end={row.original.end}
          />
        ),
      },
      {
        id: 'durationInSeconds',
        accessorKey: 'durationInSeconds',
        header: 'Duration',
        sortable: true,
        sortingFn: sortNumber('durationInSeconds'),
        meta: { align: 'right', width: 110 },
        cell: ({ getValue }) => (
          <span className="campaign-sessions-v1__duration">
            {secondsToTimeText(getValue() || 0)}
          </span>
        ),
      },
      {
        id: 'pageViews',
        accessorKey: 'pageViews',
        header: 'Pages',
        sortable: true,
        sortingFn: sortNumber('pageViews'),
        meta: { align: 'center', width: 90 },
        cell: ({ getValue }) => <PagesBadge count={getValue() || 0} />,
      },
      {
        id: 'flow',
        accessorKey: 'landingPage',
        header: 'Flow',
        sortable: false,
        meta: { align: 'left' },
        cell: ({ row }) => (
          <FlowCell
            landing={row.original.landingPage}
            exit={row.original.exitPage}
          />
        ),
      },
      {
        id: 'device',
        accessorKey: 'deviceType',
        header: 'Device',
        sortable: false,
        meta: { align: 'left', width: 120 },
        cell: ({ getValue }) => {
          const device = getValue();
          return (
            <span className="campaign-sessions-v1__device">
              <DeviceIcon device={device} />
              <span>{device || '—'}</span>
            </span>
          );
        },
      },
      {
        id: 'attrMethod',
        accessorKey: 'attrMethod',
        header: '',
        sortable: false,
        meta: { align: 'center', width: 60 },
        cell: ({ getValue }) => <AttributionIcon method={getValue() || 'IP'} />,
      },
    ],
    []
  );

  const renderMobileCard = (record) => {
    return (
      <>
        <div className="campaign-sessions-v1__card-head">
          {record.sessionId ? (
            <Tooltip overlayClassName="mjl-ds" title={record.sessionId}>
              <div className="campaign-sessions-v1__card-id">{record.sessionId}</div>
            </Tooltip>
          ) : (
            <div className="campaign-sessions-v1__card-id">—</div>
          )}
          <AttributionIcon method={record.attrMethod || 'IP'} />
        </div>

        <div className="campaign-sessions-v1__card-time">
          {formatTimeRange(record.start, record.end)}
        </div>

        <div className="campaign-sessions-v1__card-chips">
          <span className="campaign-sessions-v1__card-chip">
            <span className="campaign-sessions-v1__card-chip-label">Duration</span>
            <span className="campaign-sessions-v1__card-chip-value">
              {secondsToTimeText(record.durationInSeconds || 0)}
            </span>
          </span>
          <span className="campaign-sessions-v1__card-chip">
            <span className="campaign-sessions-v1__card-chip-label">Pages</span>
            <span className="campaign-sessions-v1__card-chip-value">
              {record.pageViews || 0}
            </span>
          </span>
          <span className="campaign-sessions-v1__card-chip">
            <span className="campaign-sessions-v1__card-chip-label">Device</span>
            <span className="campaign-sessions-v1__card-chip-value">
              <DeviceIcon device={record.deviceType} />
              <span style={{ marginLeft: 4 }}>{record.deviceType || '—'}</span>
            </span>
          </span>
        </div>

        <div className="campaign-sessions-v1__card-flow">
          <Tooltip overlayClassName="mjl-ds" title={record.landingPage || '—'}>
            <span className="campaign-sessions-v1__card-flow-page">
              {record.landingPage || '—'}
            </span>
          </Tooltip>
          <ArrowRight
            size={12}
            strokeWidth={2}
            className="campaign-sessions-v1__card-flow-arrow"
          />
          <Tooltip overlayClassName="mjl-ds" title={record.exitPage || '—'}>
            <span className="campaign-sessions-v1__card-flow-page">
              {record.exitPage || '—'}
            </span>
          </Tooltip>
        </div>
      </>
    );
  };

  // Pagination updates segment 4 of the pathname (the legacy V2 pattern —
  // route is `.../sessions/:pageNumber`). Page-size is persisted in the
  // search params (only when non-default to keep URLs clean), matching
  // the behavior the original V2 table had.
  const handlePaginationChange = ({ pageIndex, pageSize: newPageSize }) => {
    const segments = location.pathname.split('/');
    segments[4] = String(pageIndex + 1);
    const params = new URLSearchParams(location.search);
    if (newPageSize && newPageSize > 10) {
      params.set('pageSize', String(newPageSize));
    } else {
      params.delete('pageSize');
    }
    history.push({
      pathname: segments.join('/'),
      search: params.toString() ? `?${params.toString()}` : '',
    });
  };

  return (
    <section className="campaign-overview__creatives">
      <header className="campaign-overview__creatives-head">
        <div className="campaign-overview__creatives-eyebrow mono-eyebrow">
          Sessions
          <Tooltip overlayClassName="mjl-ds" title={SESSIONS_TOOLTIP}>
            <HelpCircle
              size={14}
              strokeWidth={1.75}
              style={{ marginLeft: 6, color: '#94a3b8', verticalAlign: '-2px' }}
            />
          </Tooltip>
        </div>
      </header>

      <div className="campaign-overview__creatives-body">
        <div className="campaign-overview__creatives-table">
          <DataTable
            data={rows}
            columns={columns}
            loading={loading}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 50, 100]}
            mobileBreakpoint="md"
            renderMobileCard={renderMobileCard}
            manualPagination
            pageIndex={currentPage - 1}
            totalRows={total}
            onPaginationChange={handlePaginationChange}
            className="campaign-sessions-v1__sessions-table"
            expandable={{
              isRowExpandable: () => true,
              renderExpandedRow: (record) => (
                <LazyJourneyExpand
                  record={record}
                  expandedRowsData={expandedRowsData}
                  setExpandedRowsData={setExpandedRowsData}
                  fetchSessionDetails={fetchSessionDetails}
                  sessionDetailsLoading={sessionDetailsLoading}
                />
              ),
            }}
            emptyState="No sessions to show."
          />
        </div>
      </div>
    </section>
  );
};

export default withRouter(SessionsTable);
